/** Elements that get the scroll-reveal treatment, in the order they appear per page. */
const REVEAL_SELECTOR = ".equipment-card, .fact, .link-card, .contact-card, .gallery-item";

const STAGGER_STEP_MS = 60;
const MAX_STAGGER_STEPS = 6;

/**
 * Reveals cards as they scroll into view. Elements that enter together (a grid row,
 * a card deck) cascade in with a small per-item delay instead of all snapping at
 * once — the delay is capped so a long list never leaves the last item waiting.
 *
 * Respects prefers-reduced-motion via the global CSS reset, which zeroes both the
 * transition duration and the stagger delay.
 */
export function initRevealAnimations(): void {
  const targets = document.querySelectorAll<HTMLElement>(REVEAL_SELECTOR);
  if (targets.length === 0) return;

  if (!("IntersectionObserver" in window)) {
    targets.forEach((el) => el.classList.add("is-visible"));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      // Entries that cross the threshold in the same callback belong to the same
      // "batch" visually, so stagger within the batch rather than by DOM index.
      const revealed = entries.filter((entry) => entry.isIntersecting);

      revealed.forEach((entry, index) => {
        const el = entry.target as HTMLElement;
        const step = Math.min(index, MAX_STAGGER_STEPS);
        el.style.transitionDelay = step > 0 ? `${step * STAGGER_STEP_MS}ms` : "";
        el.classList.add("is-visible");
        observer.unobserve(el);
      });
    },
    { threshold: 0.15, rootMargin: "0px 0px -40px 0px" },
  );

  targets.forEach((el) => {
    el.classList.add("reveal");
    observer.observe(el);
  });
}
